import type { WeaponDef, WeaponId, WeaponSlot } from "./schemas";

/**
 * Loadout weapons by family.
 * Starter kit stays kinetic_scatter + arc_lance; extras unlock via craft/loot.
 */
export const WEAPONS: Record<WeaponId, WeaponDef> = {
  kinetic_scatter: {
    id: "kinetic_scatter",
    name: "Kinetic Scatter",
    slot: "primary",
    cooldown: 0.18,
    projectileSpeed: 95,
    damage: 9,
    life: 0.55,
    radius: 0.45,
    spread: 0.12,
    count: 3,
    unlockRank: 1,
    description: "Close-range planar pellets. Best inside the assault envelope.",
    tradeoff: "High volume · short reach",
    projectileStyle: "pellet",
    audioProfile: "kinetic",
  },
  kinetic_slug: {
    id: "kinetic_slug",
    name: "Kinetic Slug",
    slot: "primary",
    cooldown: 0.38,
    projectileSpeed: 130,
    damage: 28,
    life: 0.85,
    radius: 0.55,
    spread: 0.02,
    count: 1,
    unlockRank: 2,
    description: "Single hard slug. Punishes mid-band lined shots.",
    tradeoff: "High damage · slow cadence",
    projectileStyle: "slug",
    audioProfile: "kinetic",
  },
  kinetic_burst: {
    id: "kinetic_burst",
    name: "Kinetic Burst",
    slot: "primary",
    cooldown: 0.28,
    projectileSpeed: 110,
    damage: 12,
    life: 0.65,
    radius: 0.4,
    spread: 0.05,
    count: 2,
    unlockRank: 3,
    description: "Tight two-round burst. Balanced poke without scatter waste.",
    tradeoff: "Steady DPS · modest spread",
    projectileStyle: "pellet",
    audioProfile: "kinetic",
  },
  needle_array: {
    id: "needle_array",
    name: "Needle Array",
    slot: "primary",
    cooldown: 0.52,
    projectileSpeed: 160,
    damage: 14,
    life: 0.78,
    radius: 0.24,
    spread: 0.035,
    count: 5,
    unlockRank: 14,
    description: "Five hypervelocity needles stitch a narrow line through distant targets.",
    tradeoff: "Long-range burst · recoil gap",
    projectileStyle: "slug",
    audioProfile: "kinetic",
  },
  cascade_cannon: {
    id: "cascade_cannon",
    name: "Cascade Cannon",
    slot: "primary",
    cooldown: 0.78,
    projectileSpeed: 104,
    damage: 46,
    life: 1.05,
    radius: 0.62,
    spread: 0,
    count: 1,
    unlockRank: 48,
    fragmentCount: 5,
    fragmentDamageRetention: 0.32,
    fragmentSpeed: 72,
    description: "A heavy primary shell bursts into five planar payload fragments on first impact.",
    tradeoff: "Impact fan · deliberate cycle",
    projectileStyle: "slug",
    audioProfile: "kinetic",
  },
  aegis_drill: {
    id: "aegis_drill",
    name: "Aegis Drill",
    slot: "primary",
    cooldown: 0.34,
    projectileSpeed: 172,
    damage: 11,
    life: 0.88,
    radius: 0.3,
    spread: 0.035,
    count: 2,
    unlockRank: 84,
    shieldDamageMult: 2.5,
    description:
      "Twin hypervelocity drill rounds deal 2.5× damage to shields without spilling that bonus into hull.",
    tradeoff: "Shield breaker · modest hull yield",
    projectileStyle: "rail",
    audioProfile: "kinetic",
  },
  arc_lance: {
    id: "arc_lance",
    name: "Arc Lance",
    slot: "secondary",
    cooldown: 0.42,
    projectileSpeed: 140,
    damage: 22,
    life: 0.7,
    radius: 0.35,
    spread: 0,
    count: 1,
    unlockRank: 1,
    description: "Fast energy bolt. Reliable mid-band poke.",
    tradeoff: "Fast bolt · single target",
    projectileStyle: "bolt",
    audioProfile: "arc",
  },
  arc_fork: {
    id: "arc_fork",
    name: "Arc Fork",
    slot: "secondary",
    cooldown: 0.55,
    projectileSpeed: 120,
    damage: 14,
    life: 0.6,
    radius: 0.32,
    spread: 0.18,
    count: 2,
    unlockRank: 4,
    description: "Twin forked bolts. Cleaves pairs and flanks.",
    tradeoff: "Two bolts · less per-hit",
    projectileStyle: "fork",
    audioProfile: "arc",
  },
  arc_pulse: {
    id: "arc_pulse",
    name: "Arc Pulse",
    slot: "secondary",
    cooldown: 0.72,
    projectileSpeed: 95,
    damage: 36,
    life: 0.9,
    radius: 0.9,
    spread: 0,
    count: 1,
    unlockRank: 6,
    description: "Fat pulse shot. Slow but knocks denser targets.",
    tradeoff: "Heavy hit · long cooldown",
    projectileStyle: "pulse",
    audioProfile: "arc",
  },
  phase_rail: {
    id: "phase_rail",
    name: "Phase Rail",
    slot: "secondary",
    cooldown: 0.9,
    projectileSpeed: 180,
    damage: 42,
    life: 1,
    radius: 0.38,
    spread: 0,
    count: 1,
    unlockRank: 20,
    pierceCount: 2,
    pierceDamageRetention: 0.82,
    description: "A line-drive phase round pierces up to three targets, shedding power each pass.",
    tradeoff: "Triple pierce · deliberate cycle",
    projectileStyle: "rail",
    audioProfile: "arc",
  },
  vortex_disc: {
    id: "vortex_disc",
    name: "Vortex Disc",
    slot: "secondary",
    cooldown: 0.82,
    projectileSpeed: 118,
    damage: 30,
    life: 1.45,
    radius: 0.62,
    spread: 0,
    count: 1,
    unlockRank: 32,
    ricochetCount: 2,
    ricochetRangeM: 24,
    ricochetDamageRetention: 0.8,
    description: "A guided cutting disc rebounds through up to three distinct nearby targets.",
    tradeoff: "Triple rebound · needs clustered targets",
    projectileStyle: "disc",
    audioProfile: "arc",
  },
  arc_web: {
    id: "arc_web",
    name: "Arc Web",
    slot: "secondary",
    cooldown: 1.05,
    projectileSpeed: 126,
    damage: 18,
    life: 1.2,
    radius: 0.38,
    spread: 0.28,
    count: 3,
    unlockRank: 52,
    ricochetCount: 1,
    ricochetRangeM: 22,
    ricochetDamageRetention: 0.76,
    description: "Three diverging arc bolts each snap once into a second nearby target.",
    tradeoff: "Six-target web · long reset",
    projectileStyle: "fork",
    audioProfile: "arc",
  },
  grav_harpoon: {
    id: "grav_harpoon",
    name: "Grav Harpoon",
    slot: "secondary",
    cooldown: 1.25,
    projectileSpeed: 150,
    damage: 52,
    life: 1.15,
    radius: 0.38,
    spread: 0,
    count: 1,
    unlockRank: 72,
    pierceCount: 1,
    pierceDamageRetention: 0.84,
    blastRadiusM: 7,
    blastDamageRetention: 0.4,
    implosionPullM: 4,
    description:
      "The first impact collapses a nearby formation toward the shot line before the harpoon pierces a second target.",
    tradeoff: "Pull then pierce · long reset",
    projectileStyle: "rail",
    audioProfile: "arc",
  },
  crown_saw: {
    id: "crown_saw",
    name: "Crown Saw",
    slot: "secondary",
    cooldown: 1.15,
    projectileSpeed: 105,
    damage: 36,
    life: 1.6,
    radius: 0.65,
    spread: 0,
    count: 1,
    unlockRank: 94,
    pierceCount: 1,
    pierceDamageRetention: 0.88,
    returnAfterS: 0.55,
    returnDamageRetention: 0.75,
    description:
      "A tethered cutting disc pierces two targets outbound, then reverses and can strike again on recall.",
    tradeoff: "Outbound pierce plus recall · deliberate cycle",
    projectileStyle: "disc",
    audioProfile: "arc",
  },
  breach_torpedo: {
    id: "breach_torpedo",
    name: "Breach Torpedo",
    slot: "ordnance",
    cooldown: 1.4,
    projectileSpeed: 48,
    damage: 55,
    life: 1.8,
    radius: 2.4,
    spread: 0,
    count: 1,
    unlockRank: 3,
    blastRadiusM: 8,
    blastDamageRetention: 0.48,
    description: "Slow AoE ordnance. Punishes clustered elites.",
    tradeoff: "Big blast · slow travel",
    projectileStyle: "torpedo",
    audioProfile: "torpedo",
  },
  swarm_microtorps: {
    id: "swarm_microtorps",
    name: "Swarm Microtorps",
    slot: "ordnance",
    cooldown: 1.1,
    projectileSpeed: 70,
    damage: 16,
    life: 1.35,
    radius: 1.1,
    spread: 0.16,
    count: 3,
    unlockRank: 9,
    description: "Three small warheads. Spreads damage across a pack.",
    tradeoff: "Multi-hit · weaker each",
    projectileStyle: "swarm",
    audioProfile: "swarm",
  },
  grav_charge: {
    id: "grav_charge",
    name: "Grav Charge",
    slot: "ordnance",
    cooldown: 2.1,
    projectileSpeed: 36,
    damage: 80,
    life: 2.4,
    radius: 3.4,
    spread: 0,
    count: 1,
    unlockRank: 12,
    blastRadiusM: 11,
    blastDamageRetention: 0.42,
    description: "Sluggish grav bomb. Huge radius, long recycle.",
    tradeoff: "Max AoE · very slow",
    projectileStyle: "charge",
    audioProfile: "charge",
  },
  implosion_charge: {
    id: "implosion_charge",
    name: "Implosion Charge",
    slot: "ordnance",
    cooldown: 1.8,
    projectileSpeed: 50,
    damage: 64,
    life: 1.7,
    radius: 0.8,
    spread: 0,
    count: 1,
    unlockRank: 38,
    blastRadiusM: 12,
    blastDamageRetention: 0.58,
    implosionPullM: 6.5,
    description:
      "A collapsed-field warhead damages a wide planar cluster and drags survivors inward.",
    tradeoff: "12 m implosion · groups follow-up targets",
    projectileStyle: "singularity",
    audioProfile: "charge",
  },
  foundry_breaker: {
    id: "foundry_breaker",
    name: "Foundry Breaker",
    slot: "ordnance",
    cooldown: 2.3,
    projectileSpeed: 46,
    damage: 76,
    life: 2.1,
    radius: 0.9,
    spread: 0,
    count: 1,
    unlockRank: 62,
    blastRadiusM: 10,
    blastDamageRetention: 0.5,
    implosionPullM: 5,
    fragmentCount: 8,
    fragmentDamageRetention: 0.24,
    fragmentSpeed: 82,
    description:
      "A foundry warhead collapses a formation inward, then ejects eight radial penetrator shards.",
    tradeoff: "Implosion plus shrapnel · long recycle",
    projectileStyle: "singularity",
    audioProfile: "charge",
  },
};

export const STARTER_LOADOUT = [
  "kinetic_scatter",
  "arc_lance",
] as const satisfies readonly WeaponId[];

export const WEAPON_IDS = Object.keys(WEAPONS) as WeaponId[];

export function weaponById(id: string): WeaponDef | undefined {
  return WEAPONS[id as WeaponId];
}

export function weaponsForSlot(slot: WeaponSlot): WeaponDef[] {
  return WEAPON_IDS.map((id) => WEAPONS[id]!).filter((w) => w.slot === slot);
}

/** Raw field-salvage value for a weapon crate that duplicates the installed gun. */
export function weaponRecycleSalvage(id: WeaponId): number {
  const unlockRank = WEAPONS[id]?.unlockRank ?? 1;
  return 12 + Math.min(36, Math.floor(unlockRank * 0.75));
}

/** Pick the equipped weapon for a fire slot from a loadout list. */
export function loadoutWeaponForSlot(
  loadout: readonly WeaponId[],
  slot: WeaponSlot,
): WeaponId | undefined {
  return loadout.find((id) => WEAPONS[id]?.slot === slot);
}

/** Compact HUD / touch label for a weapon id. */
export function shortWeaponName(id: WeaponId | undefined, fallback = "—"): string {
  if (!id) return fallback;
  const name = WEAPONS[id]?.name ?? fallback;
  if (name.startsWith("Kinetic ")) return name.slice(8, 14) || "Kin";
  if (name.startsWith("Arc ")) return name.slice(4, 10) || "Arc";
  if (name.includes("Needle")) return "Needle";
  if (name.includes("Cascade")) return "Cascade";
  if (name.includes("Aegis")) return "Aegis";
  if (name.includes("Phase")) return "Phase";
  if (name.includes("Vortex")) return "Vortex";
  if (name.includes("Web")) return "Web";
  if (name.includes("Harpoon")) return "Harpoon";
  if (name.includes("Crown")) return "Crown";
  if (name.includes("Swarm")) return "Swarm";
  if (name.includes("Grav")) return "Grav";
  if (name.includes("Implosion")) return "Implode";
  if (name.includes("Foundry")) return "Breaker";
  if (name.includes("Torpedo") || name.includes("Torp")) return "Torp";
  return name.split(" ").pop()?.slice(0, 6) || fallback;
}

const LOOT_POOL: WeaponId[] = [
  "kinetic_slug",
  "kinetic_burst",
  "needle_array",
  "cascade_cannon",
  "aegis_drill",
  "arc_fork",
  "arc_pulse",
  "phase_rail",
  "vortex_disc",
  "arc_web",
  "grav_harpoon",
  "crown_saw",
  "breach_torpedo",
  "swarm_microtorps",
  "grav_charge",
  "implosion_charge",
  "foundry_breaker",
];

/** Weighted mid-run crate / drop pick (never the starter kinetic). */
export function rollWeaponLoot(rng: () => number, pilotRank: number): WeaponId {
  // The first expedition still offers basic alternatives, while career-tier
  // weapons remain discoveries for the ranks where their designs unlock.
  const effectiveRank = Math.max(3, Math.floor(pilotRank));
  const eligible = LOOT_POOL.filter((id) => WEAPONS[id]!.unlockRank <= effectiveRank);
  const idx = Math.min(eligible.length - 1, Math.floor(rng() * eligible.length));
  return eligible[idx] ?? "kinetic_slug";
}

/** Early onboarding crate — always breach so Q/F literacy has an ordnance target. */
export const ONBOARDING_WEAPON: WeaponId = "breach_torpedo";
